import { format } from 'date-fns'
import React, { useState } from 'react'
import { Calendar } from 'src/component/ui/calendar'
import { DatePicker } from 'src/component/ui/date-picker'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from 'src/component/ui/drawer'
import { useMediaQuery } from 'src/hook/use-media-query'
import { cn } from 'src/lib/utils'
import { timeUtil } from 'src/util/time-util'

interface DateOfBirthFieldProps {
  value?: string
  onChange: (value: string) => void
  error?: string
  disabled?: boolean
  placeholder?: string
}

export const DateOfBirthField = ({
  value,
  onChange,
  error,
  disabled,
  placeholder = 'Select date of birth',
}: DateOfBirthFieldProps): React.ReactElement => {
  const isMobile = useMediaQuery('(max-width: 556px)')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const dateValue = value ? new Date(value) : undefined

  const handleSelect = (selectedDate: Date | undefined): void => {
    if (!selectedDate) return

    // Normalize the date to avoid timezone issues by setting it to noon UTC
    const normalizedDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 12, 0, 0, 0)
    onChange(timeUtil.formatInDate(normalizedDate))
    setIsDrawerOpen(false)
  }

  const today = new Date()
  today.setHours(23, 59, 59, 999)

  if (isMobile) {
    return (
      <div className="space-y-2">
        <label htmlFor="dateOfBirth" className="block text-sm font-roboto-condensed font-bold text-mist">
          Date of birth
        </label>
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'flex h-9 w-full items-center justify-start rounded-md bg-secondary-gray px-3 py-2 text-sm',
                'font-evogria font-normal text-light-gray placeholder:text-mushroom',
                'focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
                !dateValue && 'text-mushroom',
                error && 'border border-danger',
              )}
            >
              {dateValue ? (
                format(dateValue, 'dd/MM/yyyy')
              ) : (
                <p className="text-sm font-evogria text-mushroom uppercase">{placeholder}</p>
              )}
            </button>
          </DrawerTrigger>
          <DrawerContent className="bg-secondary-gray">
            <DrawerHeader className="relative">
              <DrawerTitle className="font-roboto-condensed font-bold text-mist text-center">Select Date of Birth</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4 flex justify-center">
              <Calendar
                mode="single"
                selected={dateValue}
                onSelect={handleSelect}
                defaultMonth={dateValue}
                captionLayout="dropdown"
                disabled={(date) => date > today}
              />
            </div>
          </DrawerContent>
        </Drawer>
        {error && <p className="text-sm font-roboto-condensed text-danger">{error}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <label htmlFor="dateOfBirth" className="block text-sm font-roboto-condensed font-bold text-mist">
        Date of birth
      </label>
      <DatePicker
        value={dateValue}
        onChange={(date: Date | undefined) => onChange(date ? timeUtil.formatInDate(date) : '')}
        error={Boolean(error)}
        disabled={disabled}
        placeholder={placeholder}
      />
      {error && <p className="text-sm font-roboto-condensed text-danger">{error}</p>}
    </div>
  )
}
